import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button, ButtonGroup } from "@datapay-by-datacom/design-system";
import { FileDropzone } from "../../shared/components";
import { CompanyConfigStatus, type CompanyTemplateRef } from "../../generated/core-api";
import { useBulkCreateFlow } from "../bulk-create-redesign/bulkCreateFlow";
import {
  CompanyRowsTable,
  CompanyShellExtractionNotice,
  MyPayEnvironmentSelect,
  ReplaceCompanyCsvConfirmModal,
  SelectedCompanyConfigTemplatePreview,
  type CompanyShellExtractionProvenance,
} from "../components";
import {
  deriveCompanyShellImportNotes,
  isBlockingCompanyShellParseError,
  isKnownShellCsvHeader,
  parseCompanyShellCsv,
  useCompanyConfigVersion,
  useCompanyConfigs,
  useCompanyProvisionWizard,
  useDoc2CompanyAgentId,
  useDocumentCompanyExtraction,
} from "../hooks";
import { FeatureFlag, useFeatureFlagValue } from "../../feature-flags";
import type {
  CompanyShellCsvParseError,
  CompanyShellCsvParseResult,
} from "../types";
import { BulkCreateCompanyReviewScreen } from "./BulkCreateCompanyReviewScreen";
import s from "./BulkCreateCompanyScreen.module.css";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * Questionnaire formats the Doc2Company path accepts. Kept narrower than the
 * org library's `VALID_FILE_TYPES` (which also takes PowerPoint and JSON) —
 * those are not payroll implementation questionnaires.
 */
const DOCUMENT_EXTENSIONS = [".doc", ".docx", ".pdf"] as const;

/** Matches the ingest service's own cap, which a document must pass anyway. */
const MAX_DOCUMENT_FILE_SIZE_BYTES = 50 * 1024 * 1024;

function hasDocumentExtension(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return DOCUMENT_EXTENSIONS.some((extension) => lower.endsWith(extension));
}

/**
 * Progress text per extraction stage. Named for what the user is waiting on
 * rather than for the system step — "Uploading" and "Reading" are legible;
 * "Invoking agent" and "Polling markdown" are not.
 */
const DOCUMENT_STAGE_LABELS: Record<string, string> = {
  uploading: "Uploading document…",
  extracting: "Reading the document…",
  reading: "Extracting company details…",
};

/**
 * Must match the backend's `MAX_ROWS_PER_INVOCATION` (confirmed with the
 * team, 2026-07-21) — the generated client only carries a doc comment
 * referencing that constant by name (`core-api.ts`'s `CompanyShellProvisionInput.rows`),
 * not its numeric value, so this can't be derived and has to be kept in
 * sync by hand if the backend limit ever changes.
 */
const MAX_ROWS_PER_INVOCATION = 500;

/** Composite key for a `<select>` option — `config_code` alone isn't unique across historical versions. */
function templateKey(template: CompanyTemplateRef): string {
  return `${template.config_code}::${template.version}`;
}

interface CsvIssueDetailsProps {
  errors: CompanyShellCsvParseError[];
  summary: string;
  ariaLabel: string;
  detailLabel?: string;
}

function CsvIssueDetails({
  errors,
  summary,
  ariaLabel,
  detailLabel = "Error",
}: CsvIssueDetailsProps) {
  return (
    <details className={s.issueDetails}>
      <summary className={s.issueDetailsSummary}>{summary}</summary>
      <div
        className={`${s.tableWrapper} ${s.issueTableWrapper}`}
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
      >
        <table className={s.errorsTable}>
          <thead>
            <tr>
              <th scope="col" className={s.th}>
                Row
              </th>
              <th scope="col" className={s.th}>
                Column
              </th>
              <th scope="col" className={s.th}>
                {detailLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {errors.map((error, index) => (
              <tr key={index}>
                <td className={s.td}>
                  {error.csv_line_number ?? "—"}
                </td>
                <td className={s.td}>{error.column ?? "—"}</td>
                <td className={s.td}>{error.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * Combined Upload & Review screen for bulk company creation.
 * Parses the CSV client-side, then exposes the uploaded companies for editing,
 * validation, and commit without a route change.
 */
export function BulkCreateCompanyScreen() {
  const navigate = useNavigate();
  const flow = useBulkCreateFlow();
  const isRedesign = flow.presentation === "cards";
  const { state: wizard, updateState } = useCompanyProvisionWizard();

  useEffect(() => {
    if (wizard.commitWorkflowId) {
      navigate(flow.resultsPath, { replace: true });
    }
  }, [flow.resultsPath, navigate, wizard.commitWorkflowId]);

  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CompanyShellCsvParseResult | null>(
    null
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [rejectedUpload, setRejectedUpload] = useState<{
    fileName: string;
    issues: CompanyShellCsvParseError[];
  } | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isCompanyEditing, setIsCompanyEditing] = useState(false);
  const [isReviewBusy, setIsReviewBusy] = useState(false);
  const [pendingReplacement, setPendingReplacement] = useState<File | null>(
    null
  );

  // The document path needs both the flag and the agent config: the flag gates
  // the experience, the ids gate whether it can work at all.
  const isDocumentUploadFlagged = useFeatureFlagValue(
    FeatureFlag.EnableBulkCreateCompanyDocumentUpload
  );
  const doc2Company = useDoc2CompanyAgentId();
  const isDocumentUploadEnabled =
    isDocumentUploadFlagged && doc2Company.isConfigured;
  const extraction = useDocumentCompanyExtraction();

  const template = wizard.template;
  const setTemplate = (next: CompanyTemplateRef | null) => {
    const changed =
      next?.config_code !== wizard.template?.config_code ||
      next?.version !== wizard.template?.version;
    updateState({
      template: next,
      validateStale: changed && wizard.validateResult !== null
        ? true
        : wizard.validateStale,
      validationInputRevision: changed
        ? wizard.validationInputRevision + 1
        : wizard.validationInputRevision,
    });
  };
  const mypayEnvironment = wizard.mypayEnvironment;
  const setMypayEnvironment = (next: typeof wizard.mypayEnvironment) => {
    const changed = next !== wizard.mypayEnvironment;
    updateState({
      mypayEnvironment: next,
      validateStale: changed && wizard.validateResult !== null
        ? true
        : wizard.validateStale,
      validationInputRevision: changed
        ? wizard.validationInputRevision + 1
        : wizard.validationInputRevision,
    });
  };

  const {
    data: configs,
    isLoading: isConfigsLoading,
    isError: isConfigsError,
  } = useCompanyConfigs({ status: CompanyConfigStatus.PUBLISHED });
  const {
    data: templateVersion,
    isLoading: isTemplateVersionLoading,
    isError: isTemplateVersionError,
  } = useCompanyConfigVersion(template?.config_code, template?.version);

  /** Clear the previous upload before a replacement lands (redesign only). */
  const clearPriorUpload = (nextValidationInputRevision: number) => {
    setFile(null);
    setResult(null);
    updateState({
      rows: [],
      columns: [],
      parseErrors: [],
      rowCsvLineNumbers: [],
      editedCellKeys: [],
      validateResult: null,
      validateStale: false,
      validationInputRevision: nextValidationInputRevision,
      commitWorkflowId: null,
      file: null,
      extraction: null,
    });
  };

  /**
   * Parse CSV text into wizard state.
   *
   * Shared by both upload paths: a `.csv` read with `FileReader`, and the CSV
   * the Doc2Company agent's extraction is serialised into. Keeping one
   * application point means the document path inherits every client-side check
   * — requiredness, duplicate company codes, import notes — for free.
   */
  const applyCsvText = (
    text: string,
    selected: File,
    nextValidationInputRevision: number,
    provenance: CompanyShellExtractionProvenance | null = null
  ) => {
    const parsed = parseCompanyShellCsv(text);
    const structuralIssues = parsed.parseErrors.filter(
      isBlockingCompanyShellParseError
    );
    if (isRedesign && structuralIssues.length > 0) {
      setRejectedUpload({
        fileName: selected.name,
        issues: structuralIssues,
      });
      return;
    }
    if (parsed.rows.length > MAX_ROWS_PER_INVOCATION) {
      setFileError(
        `This file has ${parsed.rows.length} companies to create — the maximum per upload is ${MAX_ROWS_PER_INVOCATION}. Split it into smaller batches.`
      );
      return;
    }
    // Replace the prior upload atomically only after the new file has been
    // read and parsed. A read error or oversized batch therefore cannot
    // destroy work already completed in Review.
    setFile(selected);
    setResult(parsed);
    // Persist the raw File alongside the parsed rows/columns — Track 2
    // (Step 16, LADR-039 / #440) needs the original file for its
    // multipart import; Track 1 only ever sends the parsed rows inline.
    updateState({
      rows: parsed.rows,
      columns: parsed.columns,
      parseErrors: parsed.parseErrors,
      rowCsvLineNumbers: parsed.rowCsvLineNumbers,
      editedCellKeys: [],
      validateResult: null,
      validateStale: false,
      validationInputRevision: nextValidationInputRevision,
      commitWorkflowId: null,
      file: selected,
      // Same update as `rows`, so a CSV upload clears a questionnaire's
      // warning and a questionnaire replaces a CSV's absence of one.
      extraction: provenance,
    });
  };

  const parseAndReplaceFile = (selected: File) => {
    const nextValidationInputRevision =
      wizard.validationInputRevision + 1;
    setPendingReplacement(null);
    setFileError(null);
    setRejectedUpload(null);
    extraction.reset();
    setIsParsing(true);
    if (isRedesign && wizard.rows.length > 0) {
      clearPriorUpload(nextValidationInputRevision);
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      setIsParsing(false);
      if (typeof text !== "string") {
        setFileError(
          "Could not read this file. Check the format and try again."
        );
        return;
      }
      applyCsvText(text, selected, nextValidationInputRevision);
    };
    reader.onerror = () => {
      setIsParsing(false);
      setFileError(
        "Could not read this file. Check the format and try again."
      );
    };
    reader.readAsText(selected);
  };

  /**
   * Document path: upload the questionnaire, have Doc2Company extract the
   * companies, then feed the result through the same CSV application as an
   * uploaded `.csv`.
   */
  const extractAndReplaceFile = async (selected: File) => {
    if (!doc2Company.agentId || !doc2Company.serviceAccountId) return;
    const nextValidationInputRevision =
      wizard.validationInputRevision + 1;
    setPendingReplacement(null);
    setFileError(null);
    setRejectedUpload(null);
    if (isRedesign && wizard.rows.length > 0) {
      clearPriorUpload(nextValidationInputRevision);
    }

    const extracted = await extraction.extract({
      file: selected,
      agentId: doc2Company.agentId,
      serviceAccountId: doc2Company.serviceAccountId,
    });
    // `null` means the run failed or was superseded; the hook owns the message.
    if (extracted === null) return;
    applyCsvText(extracted.csv, selected, nextValidationInputRevision, {
      sourceFileName: selected.name,
      missingColumns: extracted.missingColumns,
    });
  };

  const replaceFile = (selected: File) => {
    if (hasDocumentExtension(selected.name)) {
      void extractAndReplaceFile(selected);
      return;
    }
    parseAndReplaceFile(selected);
  };

  const handleFileChange = (selected: File | File[] | null) => {
    if (!selected || Array.isArray(selected)) return;
    if (isReviewBusy || wizard.commitWorkflowId) return;

    setFileError(null);
    setRejectedUpload(null);

    const isCsv = selected.name.toLowerCase().endsWith(".csv");
    const isDocument =
      isDocumentUploadEnabled && hasDocumentExtension(selected.name);
    if (!isCsv && !isDocument) {
      setFileError(
        isDocumentUploadEnabled
          ? `Only .csv, ${DOCUMENT_EXTENSIONS.join(", ")} files are accepted.`
          : "Only .csv files are accepted."
      );
      return;
    }

    // A questionnaire is sent to the ingest service, which accepts far larger
    // files than the in-browser CSV parse does.
    const maxBytes = isDocument
      ? MAX_DOCUMENT_FILE_SIZE_BYTES
      : MAX_FILE_SIZE_BYTES;
    if (selected.size > maxBytes) {
      setFileError(
        `This file is too large (max ${maxBytes / (1024 * 1024)}MB).`
      );
      return;
    }

    if (wizard.rows.length > 0) {
      setPendingReplacement(selected);
      return;
    }

    replaceFile(selected);
  };

  const hasCompanies = wizard.rows.length > 0;
  // The redesign renders Review on this same page; the two must not both show
  // the extraction warning.
  const isReviewEmbedded =
    isRedesign && hasCompanies && !wizard.commitWorkflowId;
  const csvFileIssues =
    isRedesign
      ? rejectedUpload?.issues ?? []
      : wizard.parseErrors.filter(isBlockingCompanyShellParseError);
  const importNotes = useMemo(
    () =>
      deriveCompanyShellImportNotes(
        wizard.rows,
        wizard.columns,
        wizard.parseErrors,
        wizard.rowCsvLineNumbers
      ),
    [
      wizard.columns,
      wizard.parseErrors,
      wizard.rowCsvLineNumbers,
      wizard.rows,
    ]
  );

  const environmentControl = (
    <MyPayEnvironmentSelect
      value={mypayEnvironment}
      onChange={setMypayEnvironment}
      disabled={isReviewBusy || wizard.commitWorkflowId !== null}
      productName={isRedesign ? "DataPay" : "Datapay"}
    />
  );
  const templateControl = (
    <>
      {isConfigsLoading && (
        <p className={s.selectedFile}>Loading configuration templates…</p>
      )}
      {isConfigsError && (
        <p className={s.warning} role="alert">
          Could not load configuration templates.
        </p>
      )}
      {configs && (
        <select
          className={s.select}
          aria-label="Select a configuration template"
          value={template ? templateKey(template) : ""}
          disabled={isReviewBusy || wizard.commitWorkflowId !== null}
          onChange={(e) => {
            const config = configs.find(
              (candidate) => templateKey(candidate) === e.target.value
            );
            setTemplate(
              config
                ? { config_code: config.config_code, version: config.version }
                : null
            );
          }}
        >
          <option value="">— Select a template —</option>
          {configs.map((config) => (
            <option key={templateKey(config)} value={templateKey(config)}>
              {config.display_name}
              {isRedesign ? ` (v${config.version})` : ""}
            </option>
          ))}
        </select>
      )}
    </>
  );
  const provisioningOptions = isRedesign ? (
    <section
      className={`${s.section} ${s.provisioning}`}
      aria-label="Provisioning options"
    >
      <div className={s.provisioningGrid}>
        <div className={s.provisioningField}>
          <h3 id="environment-heading" className={s.fieldHeading}>
            DataPay environment
          </h3>
          {environmentControl}
        </div>

        <div className={s.provisioningField}>
          <h3 id="template-heading" className={s.fieldHeading}>
            Configuration template
          </h3>
          {templateControl}
        </div>
      </div>
    </section>
  ) : (
    <>
      <section className={s.section} aria-labelledby="environment-heading">
        <h2 id="environment-heading" className={s.sectionHeading}>
          Datapay environment
        </h2>
        <p className={s.selectedFile}>
          Choose which MyPay API deployment to provision companies against.
        </p>
        {environmentControl}
      </section>
      <section className={s.section} aria-labelledby="template-heading">
        <h2 id="template-heading" className={s.sectionHeading}>
          Configuration template
        </h2>
        {templateControl}
      </section>
    </>
  );

  return (
    <div className={s.container}>
      <header className={s.header}>
        <h1 className={s.title}>{flow.title}</h1>
        <p className={s.subtitle}>
          {isDocumentUploadEnabled
            ? "Upload a company-shell CSV or a completed questionnaire, review and edit the companies, then validate them before creation."
            : "Upload a company-shell CSV, review and edit the companies, then validate them before creation."}
        </p>
      </header>

      {!isRedesign && provisioningOptions}

      <section className={s.section} aria-labelledby="upload-heading">
        <h2 id="upload-heading" className={s.sectionHeading}>
          Select file
        </h2>
        <FileDropzone
          value={file}
          compactWhenSelected={isRedesign}
          onChange={handleFileChange}
          instruction={
            isDocumentUploadEnabled
              ? "Drop your CSV or questionnaire here, or click to browse"
              : "Drop your CSV file here, or click to browse"
          }
          subInstruction={
            isDocumentUploadEnabled
              ? `Accepted formats: CSV (.csv) up to 5MB, or a completed questionnaire (${DOCUMENT_EXTENSIONS.join(
                  ", "
                )}) up to ${MAX_DOCUMENT_FILE_SIZE_BYTES / (1024 * 1024)}MB. Maximum ${MAX_ROWS_PER_INVOCATION} companies.`
              : `Accepted format: CSV (.csv), up to 5MB and ${MAX_ROWS_PER_INVOCATION} companies.`
          }
          accept={
            isDocumentUploadEnabled
              ? [".csv", ...DOCUMENT_EXTENSIONS].join(",")
              : ".csv"
          }
          multiple={false}
          isDisabled={
            isParsing ||
            extraction.isRunning ||
            isReviewBusy ||
            wizard.commitWorkflowId !== null ||
            (isRedesign &&
              (isCompanyEditing || pendingReplacement !== null))
          }
          buttonText={
            isDocumentUploadEnabled ? "Select file" : "Select CSV file"
          }
          disabledButtonText={
            isReviewBusy || wizard.commitWorkflowId !== null
              ? "Workflow in progress"
              : isRedesign && isCompanyEditing
              ? "Finish editing first"
              : isRedesign && pendingReplacement
                ? "Choose whether to replace"
                : extraction.isRunning
                  ? "Reading document..."
                  : "Parsing..."
          }
        />
        {!isRedesign && file && !fileError && (
          <p className={s.selectedFile}>
            Selected: <strong>{file.name}</strong> (
            {(file.size / 1024).toFixed(1)} KB)
          </p>
        )}
        {isRedesign && isCompanyEditing && (
          <p className={s.selectedFile} role="status">
            Save or cancel company editing before replacing the CSV file.
          </p>
        )}
        {isParsing && (
          <p className={s.selectedFile} role="status">
            Parsing file…
          </p>
        )}
        {extraction.isRunning && (
          <p className={s.extractionProgress} role="status">
            {DOCUMENT_STAGE_LABELS[extraction.stage]}
          </p>
        )}
        {extraction.stage === "error" && extraction.error && (
          <p className={s.warning} role="alert">
            {extraction.error}
          </p>
        )}
        {fileError && (
          <p className={s.warning} role="alert">
            {fileError}
          </p>
        )}
      </section>

      {/*
        Read from the batch, not from this run's extraction state, so the
        warning survives leaving and returning to Upload. Suppressed where
        Review is embedded below, which renders the same notice itself.
      */}
      {!isReviewEmbedded && (
        <CompanyShellExtractionNotice provenance={wizard.extraction} />
      )}

      {isRedesign && result && result.rows.length > 0 && (
        <p className={s.selectedFile} role="status">
          <strong>
            {result.rows.length} compan
            {result.rows.length === 1 ? "y" : "ies"} parsed
          </strong>
          . Review and edit the uploaded details below.
        </p>
      )}

      {csvFileIssues.length > 0 && (
        <section
          className={s.section}
          aria-labelledby="csv-file-issues-heading"
        >
          <div className={s.warning} role="alert">
            <h2
              id="csv-file-issues-heading"
              className={s.parseSummaryHeading}
            >
              {isRedesign
                ? `File not imported — ${csvFileIssues.length} structural issue${
                    csvFileIssues.length === 1 ? "" : "s"
                  }`
                : `${csvFileIssues.length} CSV file issue${
                    csvFileIssues.length === 1 ? "" : "s"
                  }`}
            </h2>
            {isRedesign ? (
              <>
                <p className={s.parseGuidance}>
                  No companies were added. Structural issues mean columns or
                  extra values cannot be mapped safely.
                </p>
                <p className={s.parseGuidance}>
                  Fix the listed issues in{" "}
                  {rejectedUpload?.fileName ?? "the CSV"} and upload the file
                  again.
                </p>
              </>
            ) : (
              <p className={s.parseGuidance}>
                Fix{" "}
                {csvFileIssues.length === 1 ? "this issue" : "these issues"} in
                the CSV and upload the file again before validating.
              </p>
            )}
          </div>
          <CsvIssueDetails
            errors={csvFileIssues}
            summary={`View ${csvFileIssues.length} CSV file issue${
              csvFileIssues.length === 1 ? "" : "s"
            }`}
            ariaLabel="CSV file issues"
          />
        </section>
      )}

      {importNotes.length > 0 && (
        <section
          className={s.section}
          aria-labelledby="csv-import-notes-heading"
        >
          <div className={s.notice} role="status">
            <h2
              id="csv-import-notes-heading"
              className={s.parseSummaryHeading}
            >
              {importNotes.length} import note
              {importNotes.length === 1 ? "" : "s"}
            </h2>
            {isRedesign && (
              <p className={s.parseGuidance}>
                These companies were imported because their columns could be
                mapped safely.
              </p>
            )}
            <p className={s.parseGuidance}>
              Fix these rows in the CSV and re-upload it, or correct the
              highlighted company fields in Review below.
            </p>
          </div>
          <CsvIssueDetails
            errors={importNotes}
            summary={`View ${importNotes.length} import note${
              importNotes.length === 1 ? "" : "s"
            }`}
            ariaLabel="CSV import notes"
            detailLabel="Details"
          />
        </section>
      )}

      {result && result.rows.length === 0 && result.parseErrors.length === 0 && (
        <p className={s.warning} role="alert">
          The CSV file contains no data rows.
        </p>
      )}

      {isRedesign && !hasCompanies && provisioningOptions}

      {isRedesign && !hasCompanies && template && (
        <SelectedCompanyConfigTemplatePreview
          templateVersion={templateVersion}
          isLoading={isTemplateVersionLoading}
          isError={isTemplateVersionError}
          presentation="review"
        />
      )}

      {isReviewEmbedded && (
        <BulkCreateCompanyReviewScreen
          embedded
          provisioningOptions={provisioningOptions}
          onCompanyEditingChange={setIsCompanyEditing}
          onOperationBusyChange={setIsReviewBusy}
          isExternallyBusy={
            isParsing || extraction.isRunning || pendingReplacement !== null
          }
        />
      )}

      {!isRedesign && hasCompanies && (
        <section className={s.section} aria-labelledby="preview-heading">
          <h2 id="preview-heading" className={s.sectionHeading}>
            {wizard.rows.length} compan
            {wizard.rows.length === 1 ? "y" : "ies"} parsed
          </h2>
          <CompanyRowsTable
            columns={wizard.columns}
            rows={wizard.rows}
            ariaLabel="Parsed companies preview"
          />
        </section>
      )}

      {!isRedesign && (
        <ButtonGroup align="right">
          <Button
            variant="primary"
            isDisabled={
              !hasCompanies ||
              // An extraction in flight still resolves after navigation; its
              // result would land on a batch the user is already reviewing.
              extraction.isRunning ||
              csvFileIssues.length > 0 ||
              wizard.columns.some(
                (column) => !isKnownShellCsvHeader(column.header)
              ) ||
              template === null ||
              mypayEnvironment === null
            }
            onPress={() => navigate(flow.reviewPath)}
          >
            Continue
          </Button>
        </ButtonGroup>
      )}

      <ReplaceCompanyCsvConfirmModal
        isOpen={pendingReplacement !== null}
        companyCount={wizard.rows.length}
        fileName={pendingReplacement?.name ?? ""}
        onCancel={() => setPendingReplacement(null)}
        onReplace={() => {
          if (pendingReplacement) {
            replaceFile(pendingReplacement);
          }
        }}
      />
    </div>
  );
}
